import { useState, useCallback } from 'react';
import { useWorkspaceStore } from '../../../../stores/workspaceStore';
import { useToast } from '../../../../hooks/useToast';
import { useInvalidateQueries } from '../../../../hooks/useApi';
import { knowledgeService } from '../../../../services/knowledgeService';
import type {
  CrawlSingleUrlRequest,
  CrawlSitemapRequest,
} from '../../../../services/knowledgeService';

interface UseAddUrlReturn {
  crawlSingleUrl: (data: CrawlSingleUrlRequest) => Promise<void>;
  crawlSitemap: (data: CrawlSitemapRequest) => Promise<void>;
  isLoading: boolean;
  error: string | null;
  isSuccess: boolean;
  reset: () => void;
}

export function useAddUrl(): UseAddUrlReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const workspaceId = useWorkspaceStore((s) => s.currentWorkspace?.id);
  const toast = useToast();
  const invalidate = useInvalidateQueries();

  const reset = useCallback(() => {
    setIsLoading(false);
    setError(null);
    setIsSuccess(false);
  }, []);

  const crawlSingleUrl = useCallback(
    async (data: CrawlSingleUrlRequest) => {
      if (!workspaceId) {
        toast.error('Error', 'No workspace selected.');
        return;
      }

      setIsLoading(true);
      setError(null);
      setIsSuccess(false);

      try {
        await knowledgeService.crawlUrl(workspaceId, data);
        setIsSuccess(true);
        await invalidate(['knowledge-documents']);
        toast.success('Crawl queued', 'The URL will be crawled and processed shortly.');
      } catch (err: any) {
        const message =
          err?.response?.data?.message || err?.message || 'Failed to queue crawl job.';
        setError(message);
        toast.error('Crawl failed', message);
      } finally {
        setIsLoading(false);
      }
    },
    [workspaceId, toast, invalidate]
  );

  const crawlSitemap = useCallback(
    async (data: CrawlSitemapRequest) => {
      if (!workspaceId) {
        toast.error('Error', 'No workspace selected.');
        return;
      }

      setIsLoading(true);
      setError(null);
      setIsSuccess(false);

      try {
        await knowledgeService.crawlSitemap(workspaceId, data);
        setIsSuccess(true);
        await invalidate(['knowledge-documents']);
        toast.success(
          'Sitemap crawl queued',
          'The sitemap will be parsed and pages crawled shortly.'
        );
      } catch (err: any) {
        const message =
          err?.response?.data?.message || err?.message || 'Failed to queue sitemap crawl.';
        setError(message);
        toast.error('Crawl failed', message);
      } finally {
        setIsLoading(false);
      }
    },
    [workspaceId, toast, invalidate]
  );

  return {
    crawlSingleUrl,
    crawlSitemap,
    isLoading,
    error,
    isSuccess,
    reset,
  };
}